import { useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip, Button, CircularProgress } from '@material-ui/core';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useDispatch } from 'react-redux';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';
import ConfirmDialog from '../ConfirmDialog';
import useVrackApi from '../../hooks/useVrackApi';

RemoveCompanyAdminButton.propTypes = {
  variant: PropTypes.string,
  email: PropTypes.string.isRequired,
  companyId: PropTypes.string.isRequired,
  updateAdmins: PropTypes.func.isRequired,
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object])),
    PropTypes.func,
    PropTypes.object
  ]),
  disabled: PropTypes.bool
};

export default function RemoveCompanyAdminButton({
  variant,
  email,
  companyId,
  updateAdmins,
  sx = [],
  disabled
}) {
  const dispatch = useDispatch();
  const [confirmDialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: removeCompanyAdminApi } = useVrackApi(
    'delete',
    `/companies/${companyId}/users`
  );

  const handleDialogCancel = () => {
    if (loading) return;
    setDialogOpen(false);
  };

  const handleDialogConfirm = async () => {
    if (loading) return;
    setLoading(true);
    const reqBody = {
      username: email,
      role: 'admin'
    };
    try {
      await removeCompanyAdminApi({ data: reqBody });
      openSnackbar({ message: 'Admin removed from company successfully!' });
      updateAdmins();
      setDialogOpen(false);
    } catch (error) {
      openSnackbar({ message: 'Failed to remove admin from company', severity: 'error' });
    }
    setLoading(false);
  };

  const handleDialogOpen = (event) => {
    event.stopPropagation();
    if (disabled || loading) return;
    setDialogOpen(true);
  };

  return (
    <>
      <ConfirmDialog
        title="Remove Admin?"
        content="Are you sure you wish to remove this admin from the company?"
        onCancelHandler={handleDialogCancel}
        onConfirmHandler={handleDialogConfirm}
        isOpen={confirmDialogOpen}
        confirming={loading}
      />
      {variant === 'icononly' && (
        <Tooltip title="Remove Admin">
          <IconButton
            onClick={handleDialogOpen}
            sx={{ ...sx }}
            disabled={disabled || loading}
            color="primary"
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" data-testid="delete-icon-loading" />
            ) : (
              <RemoveCircleOutlineIcon data-testid="delete-icon" />
            )}
          </IconButton>
        </Tooltip>
      )}
      {variant === 'text' && (
        <Tooltip title="Remove Admin">
          <Button
            onClick={handleDialogOpen}
            variant="contained"
            sx={{ ...sx }}
            disabled={disabled || loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <RemoveCircleOutlineIcon />
            }
          >
            Remove Admin
          </Button>
        </Tooltip>
      )}
    </>
  );
}
